import React, { useCallback, useEffect, useState } from 'react';
import { ArchiveRestore } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/ui/button';
import { useLiveClientViews } from 'src/hooks/use-live-client-views';
import ReportComponentWrapper from 'src/pages/client-management/components/manage-clients/customize-view/report-component-wrapper';
import {
  ReportViewCustomizeContextType,
  useReportViewCustomize,
} from 'src/pages/client-management/components/manage-clients/manage-client-views';
import {
  handleComponentDefaultMetricSelection,
  handleMetricUniversalDisplayDefaultSelection,
} from 'src/pages/client-management/utils/client-management-utils';
import ConversionsByBeaconTable from 'src/pages/reports/components/conversions-by-beacon-table';
import MetricBreakdownTable from 'src/pages/reports/components/metric-breakdown-table/metric-breakdown-table';
import MetricPercentDisplay from 'src/pages/reports/components/metric-percent-chart/metric-percent-chart';
import MetricSourceRank, {
  MetricRankDisplayData,
} from 'src/pages/reports/components/metric-source-display/metric-rank-display';
import MetricUniversalDisplayCard from 'src/pages/reports/components/metric-universal-display/metric-universal-display-card';
import ReportingApiV1FilterBar, {
  ReportingApiV1FilterSelection,
} from 'src/pages/reports/components/reporting-api-v1-filter-bar';
import ReportingEmptyState from 'src/pages/reports/components/reporting-empty-state';
import ZipcodeHeatmapDisplay from 'src/pages/reports/components/metric-zipcode-display/ZipcodeHeatmapDisplay';
import { ZipcodeMetrics } from 'src/pages/reports/components/metric-zipcode-display/types';
import {
  useGetReportingApiV1AppMutation,
  useGetReportingApiV1BreakdownMutation,
  useGetReportingApiV1ConversionsByBeaconMutation,
  useGetReportingApiV1DomainMutation,
  useGetReportingApiV1GeoMutation,
  useGetReportingApiV1MetricsMutation,
  useGetReportingApiV1TimeSeriesMutation,
} from 'src/redux/admin/reporting-api-v1/reporting-api-v1.endpoints';
import {
  REPORTING_API_V1_EMPTY_MESSAGE,
  ReportingApiV1TimeSeriesPoint,
} from 'src/redux/admin/reporting-api-v1/reporting-api-v1.types';
import {
  calculateVideoCompletionRateDifference,
  calculateVideoCompletionRatePercent,
} from 'src/pages/reports/utils/calculated-values.utils';
import { FALLBACK_DEFAULT_CLIENT, ClientView } from 'src/redux/organization/organization.types';
import { ReduxState } from 'src/redux/redux-state';
import { RootState } from 'src/redux/redux-store';

interface ReportsTabProps {
  showOldVersionButton: boolean;
  onSelectionChange?: (selection: ReportingApiV1FilterSelection) => void;
}

const sanitizeClientId = (value?: string | null): string | undefined => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
  return trimmed;
};

const ReportsTab: React.FC<ReportsTabProps> = ({
  showOldVersionButton,
  onSelectionChange = () => {},
}) => {
  const userState = useSelector((state: ReduxState) => state.userState);
  const activeClient = useSelector((state: RootState) => state.userState.activeClient);
  const urlParams = new URLSearchParams(location.search);
  const previewClientId = sanitizeClientId(urlParams.get('previewClientId'));
  const isClientView =
    userState.permissions?.groups?.includes('client_user') ||
    userState.permissions?.groups?.includes('client_admin');
  const extClientId = isClientView
    ? undefined
    : previewClientId || sanitizeClientId(activeClient?.ext_client_id);

  const { clientViews } = useLiveClientViews({ previewClientId: previewClientId ?? null });

  const [selection, setSelection] = useState<ReportingApiV1FilterSelection>({
    source: '',
    accounts: [],
    campaigns: [],
    lineItems: [],
    dateFilter: '',
    startDate: '',
    endDate: '',
  });
  const [formattedTimeSeriesData, setFormattedTimeSeriesData] = useState<
  ReportingApiV1TimeSeriesPoint[]
  >([]);

  const customizeContext = useReportViewCustomize();
  const navigate = useNavigate();

  const [fetchReportMetrics, { data: metricsData, isLoading: isLoadingMetrics, error: metricsError }] =
    useGetReportingApiV1MetricsMutation();
  const [
    fetchTimeSeriesData,
    { data: rawTimeSeriesData, isLoading: isLoadingTimeSeries, error: timeSeriesError },
  ] = useGetReportingApiV1TimeSeriesMutation();
  const [fetchGeoData, { data: geoData, isLoading: isLoadingGeoData, error: geoError }] =
    useGetReportingApiV1GeoMutation();
  const [fetchDomainData, { data: rawDomainData, isLoading: domainLoading, error: domainError }] =
    useGetReportingApiV1DomainMutation();
  const [fetchAppData, { data: rawAppData, isLoading: appLoading, error: appError }] =
    useGetReportingApiV1AppMutation();
  const [
    fetchBreakdownData,
    { data: rawPerformanceData, isLoading: isLoadingPerformanceData, error: breakdownError },
  ] = useGetReportingApiV1BreakdownMutation();
  const [
    fetchConversionsByBeaconData,
    {
      data: conversionsByBeaconData = [],
      isLoading: isLoadingConversionsByBeacon,
      isSuccess: isConversionsByBeaconSuccess,
      error: conversionsByBeaconError,
    },
  ] = useGetReportingApiV1ConversionsByBeaconMutation();

  const getCurrentClientView = useCallback(() => {
    if (!selection.accounts.length) return FALLBACK_DEFAULT_CLIENT;

    const findView = (views: ClientView[] | undefined) => {
      if (!views) return null;

      if (selection.accounts.length > 1) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }

      const currentView = views.find(
        (view: ClientView) => view.account_ext_id === selection.accounts[0].id.toString()
      );
      const notConfigured =
        currentView &&
        !currentView.metrics?.length &&
        !currentView.graph_one?.metric_one &&
        !currentView.graph_one?.metric_two &&
        !currentView.graph_two?.metric_one &&
        !currentView.graph_two?.metric_two &&
        !currentView.graph_three?.metric_one &&
        !currentView.graph_three?.metric_two;

      if (notConfigured) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }
      return currentView;
    };

    if (previewClientId) {
      return (findView(clientViews) as ClientView) ?? FALLBACK_DEFAULT_CLIENT;
    }

    if (!isClientView) return FALLBACK_DEFAULT_CLIENT;
    return (findView(clientViews) as ClientView) ?? FALLBACK_DEFAULT_CLIENT;
  }, [isClientView, selection.accounts, clientViews, previewClientId]);

  const shouldShowMetric = useCallback(
    (metric: string) => {
      if (isClientView || previewClientId) {
        const currentView = getCurrentClientView();
        const included = currentView?.metrics.includes(metric) ?? false;
        if (!included) return false;
      }

      if (metric === 'geo') {
        return (geoData?.length || 0) > 0 || isLoadingGeoData;
      }
      if (metric === 'domain') {
        return (rawDomainData?.length || 0) > 0 || domainLoading;
      }
      if (metric === 'app') {
        return (rawAppData?.length || 0) > 0 || appLoading;
      }
      if (metric === 'video_completion_rate') {
        return !!(metricsData?.current && metricsData.current.video_starts > 0) || isLoadingMetrics;
      }
      // Audio is stubbed on /api/v1 (always 0) until mart columns exist — show empty state.
      if (metric === 'audio_completion_rate') {
        return true;
      }
      if (metric === 'conversions_by_beacon') {
        return (
          conversionsByBeaconData.length > 0
          || isLoadingConversionsByBeacon
          || isConversionsByBeaconSuccess
        );
      }
      // Not available on /api/v1 — keep hidden via client views; otherwise always empty-state.
      if (metric === 'hourly') {
        return true;
      }

      if (!metricsData?.current) return true;
      const value = metricsData.current[metric];
      return typeof value !== 'undefined' && value !== 0;
    },
    [
      isClientView,
      previewClientId,
      getCurrentClientView,
      metricsData,
      geoData,
      rawDomainData,
      rawAppData,
      isLoadingGeoData,
      domainLoading,
      appLoading,
      conversionsByBeaconData,
      isLoadingConversionsByBeacon,
      isConversionsByBeaconSuccess,
    ]
  );

  useEffect(() => {
    if (
      selection.source &&
      selection.accounts.length > 0 &&
      selection.startDate &&
      selection.endDate
    ) {
      const reportParams = {
        source: selection.source,
        accountIds: selection.accounts.map((account) => account.id),
        campaignIds: selection.campaigns.map((campaign) => campaign.id),
        lineItemIds: selection.lineItems.map((lineItem) => lineItem.id),
        startDate: selection.startDate,
        endDate: selection.endDate,
        extClientId,
      };

      fetchReportMetrics(reportParams);
      fetchTimeSeriesData(reportParams);
      fetchGeoData(reportParams);
      fetchDomainData(reportParams);
      fetchAppData(reportParams);
      fetchBreakdownData(reportParams);
      fetchConversionsByBeaconData(reportParams);
    }
  }, [
    selection,
    extClientId,
    fetchReportMetrics,
    fetchTimeSeriesData,
    fetchGeoData,
    fetchDomainData,
    fetchAppData,
    fetchBreakdownData,
    fetchConversionsByBeaconData,
  ]);

  useEffect(() => {
    if (rawTimeSeriesData) {
      setFormattedTimeSeriesData(rawTimeSeriesData);
    } else {
      setFormattedTimeSeriesData([]);
    }
  }, [rawTimeSeriesData]);

  const handleSelectionChange = (next: ReportingApiV1FilterSelection) => {
    setSelection(next);
    onSelectionChange(next);
  };

  const hasSelection =
    Boolean(selection.source) &&
    selection.accounts.length > 0 &&
    Boolean(selection.startDate) &&
    Boolean(selection.endDate);

  const metricsFailed = Boolean(metricsError || timeSeriesError);
  const showMetricsEmpty = hasSelection && !isLoadingMetrics && !isLoadingTimeSeries && metricsFailed;

  return (
    <div className="h-full flex flex-col space-y-4 p-6 pt-0 gap-4 pb-10">
      <div className="flex-1 space-y-8 pb-[100px]">
        {showOldVersionButton && (
          <div className="relative">
            <Button
              className="absolute right-0 -top-2"
              variant="outline"
              size="sm"
              onClick={() => navigate('/reports-old')}
            >
              <ArchiveRestore className="w-4 h-4" />
              Old Version
            </Button>
          </div>
        )}
        <ReportingApiV1FilterBar
          className="bg-gray-201 text-black rounded-full"
          onSelectionChange={handleSelectionChange}
        />
        <div style={{ marginTop: '14px' }}>
          <ReportComponentWrapper
            componentType="metric-universal-display"
            maxDefaults={4}
            defaults={false}
          >
            {showMetricsEmpty ? (
              <ReportingEmptyState />
            ) : (
              <MetricUniversalDisplayCard
                metricsData={metricsData as any}
                isLoading={isLoadingMetrics || isLoadingTimeSeries}
                aggregateData={
                  metricsData?.current || {
                    impressions: 0,
                    spend: 0,
                    conversions: 0,
                    clicks: 0,
                  }
                }
                timeSeriesData={formattedTimeSeriesData as any}
                startDate={selection.startDate}
                endDate={selection.endDate}
                handlePlatformClick={(metric, selectedMetricName) =>
                  handleMetricUniversalDisplayDefaultSelection(
                    customizeContext as ReportViewCustomizeContextType,
                    metric,
                    selectedMetricName ?? ''
                  )
                }
                handleDownload={() => undefined}
              />
            )}
          </ReportComponentWrapper>
        </div>

        <div className="flex justify-between w-full gap-8">
          <ReportComponentWrapper
            componentType="zipcode-heatmap"
            className="w-3/4"
            bottom={true}
            defaults={false}
          >
            {geoError && !isLoadingGeoData ? (
              <ReportingEmptyState
                className="w-3/4"
                title="Zipcode Heatmap"
              />
            ) : (
              <ZipcodeHeatmapDisplay
                className="w-3/4"
                data={
                  geoData && geoData.length > 0
                    ? (geoData as unknown as ZipcodeMetrics[])
                    : []
                }
                isLoading={isLoadingGeoData}
                config={{
                  selectedMetric: 'impressions',
                  showLegend: true,
                  showTooltips: true,
                  mapHeight: 400,
                }}
              />
            )}
          </ReportComponentWrapper>
          <ReportComponentWrapper
            componentType="zipcode-rank"
            className="w-1/4"
            bottom={true}
            defaults={false}
          >
            {geoError && !isLoadingGeoData ? (
              <ReportingEmptyState
                className="w-1/4"
                title="Zipcode"
              />
            ) : (
              <MetricSourceRank
                title="Zipcode"
                labelKey="Zipcode"
                onMetricChange={(metric) =>
                  handleComponentDefaultMetricSelection(
                    customizeContext as ReportViewCustomizeContextType,
                    'zipcode-rank',
                    metric
                  )
                }
                data={
                  geoData && geoData.length > 0
                    ? (geoData as unknown as MetricRankDisplayData[])
                    : []
                }
                isLoading={isLoadingGeoData}
                className="w-1/4"
              />
            )}
          </ReportComponentWrapper>
        </div>

        <div>
          <ReportComponentWrapper
            componentType="time-of-day"
            className="w-full"
            defaults={false}
          >
            <ReportingEmptyState title="Hourly Performance" />
          </ReportComponentWrapper>
        </div>

        <div className="flex w-full gap-8">
          <ReportComponentWrapper
            componentType="domain-rank"
            className="w-1/3"
            defaults={false}
          >
            {domainError && !domainLoading ? (
              <ReportingEmptyState
                className="w-1/3"
                title="Domain"
              />
            ) : (
              <MetricSourceRank
                title="Domain"
                labelKey="Domain"
                data={
                  rawDomainData
                    ? (rawDomainData as unknown as MetricRankDisplayData[])
                    : []
                }
                isLoading={domainLoading}
                initialMetric="impressions"
                className="w-1/3"
              />
            )}
          </ReportComponentWrapper>

          <ReportComponentWrapper
            componentType="app-rank"
            className="w-1/3"
          >
            {appError && !appLoading ? (
              <ReportingEmptyState
                className="w-1/3"
                title="App"
              />
            ) : (
              <MetricSourceRank
                title="App"
                labelKey="App"
                data={rawAppData ? (rawAppData as unknown as MetricRankDisplayData[]) : []}
                isLoading={appLoading}
                initialMetric="impressions"
                className="w-1/3"
              />
            )}
          </ReportComponentWrapper>
        </div>

        <div className="flex w-full gap-8">
          <ReportComponentWrapper
            componentType="video-completion-rate"
            fieldSelection={false}
            className="w-1/2"
          >
            <MetricPercentDisplay
              title="Video Completion Rate"
              percentage={calculateVideoCompletionRatePercent({
                completes: metricsData?.current?.video_completes ?? 0,
                starts: metricsData?.current?.video_starts ?? 0,
                impressions: metricsData?.current?.impressions ?? 0,
                source: selection.source,
              })}
              percentChange={calculateVideoCompletionRateDifference({
                completes: metricsData?.current?.video_completes ?? 0,
                starts: metricsData?.current?.video_starts ?? 0,
                impressions: metricsData?.current?.impressions ?? 0,
                difCompletes: metricsData?.dif?.video_completes ?? 0,
                difStarts: metricsData?.dif?.video_starts ?? 0,
                difImpressions: metricsData?.dif?.impressions ?? 0,
                source: selection.source,
              })}
              isLoading={isLoadingMetrics}
              emptyStateMessage={REPORTING_API_V1_EMPTY_MESSAGE}
            />
          </ReportComponentWrapper>
          <ReportComponentWrapper
            componentType="audio-completion-rate"
            fieldSelection={false}
            className="w-1/2"
          >
            <MetricPercentDisplay
              title="Audio Completion Rate"
              percentage={
                (metricsData?.current?.audio_starts ?? 0) > 0
                  ? ((metricsData?.current?.audio_completes ?? 0) /
                      (metricsData?.current?.audio_starts ?? 1)) *
                    100
                  : 0
              }
              percentChange={0}
              isLoading={isLoadingMetrics}
              emptyStateMessage={REPORTING_API_V1_EMPTY_MESSAGE}
            />
          </ReportComponentWrapper>
        </div>

        <div className="flex w-full gap-8">
          <ReportComponentWrapper
            componentType="breakdown-table"
            fieldSelection={false}
            className="w-full"
          >
            {breakdownError && !isLoadingPerformanceData ? (
              <ReportingEmptyState title="Breakdown" />
            ) : (
              <MetricBreakdownTable
                data={(rawPerformanceData || []) as any}
                isLoading={isLoadingPerformanceData}
                shouldShowMetric={shouldShowMetric as (metric: string) => boolean}
                handleDownload={() => undefined}
                source={selection.source}
              />
            )}
          </ReportComponentWrapper>
        </div>

        {shouldShowMetric('conversions_by_beacon') && (
          <div className="flex w-full gap-8">
            <ReportComponentWrapper
              componentType="conversions-by-beacon"
              fieldSelection={false}
              className="w-full"
            >
              {conversionsByBeaconError && !isLoadingConversionsByBeacon ? (
                <ReportingEmptyState title="Conversions by Beacon" />
              ) : (
                <ConversionsByBeaconTable
                  data={conversionsByBeaconData}
                  isLoading={isLoadingConversionsByBeacon}
                />
              )}
            </ReportComponentWrapper>
          </div>
        )}
      </div>
      <div className="mb-10" />
    </div>
  );
};

export default ReportsTab;
